import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { apiGet } from '../lib/api';

type Certificate = {
  lot_code: string;
  crop_type: string;
  variety?: string;
  harvest_date?: string;
  total_quantity_kg?: number;
  quality_grade?: string;
  traceability_status?: string;
  cohort_name?: string;
  tree_count?: number;
  farmer_count?: number;
  verified_by?: string;
};

type TimelineItem = {
  event_type: string;
  event_title: string;
  event_description?: string;
  event_date?: string;
};

const formatDate = (value?: string) => {
  if (!value) return 'Pending';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const PublicTraceCertificatePage: React.FC = () => {
  const { code } = useParams();
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiGet<{ success: boolean; certificate: Certificate; timeline: TimelineItem[] }>(`/api/traceability/public/${encodeURIComponent(code || '')}`)
      .then((data) => {
        if (!alive) return;
        setCertificate(data.certificate);
        setTimeline(data.timeline || []);
      })
      .catch(() => {
        if (alive) setError('Traceability certificate was not found.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => { alive = false; };
  }, [code]);

  return (
    <>
      <SEO
        title={certificate ? `${certificate.lot_code} Traceability Certificate | eSanza` : 'Traceability Certificate | eSanza'}
        description="Public eSanza crop traceability certificate for verified harvest lots."
      />
      <main className="min-h-screen bg-[#f7f9f4] text-[#102016]">
        <section className="bg-[#06150e] px-5 py-8 text-white lg:px-8">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img src="/images/esanzaicon.png" alt="eSanza" className="h-11 w-11 rounded-lg bg-white p-1 object-contain" />
              <span className="text-lg font-black">eSanza</span>
            </Link>
            <span className="rounded-full border border-white/15 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.18em] text-[#d4af37]">
              Public trace
            </span>
          </div>
        </section>

        <section className="px-5 py-12 lg:px-8">
          <div className="mx-auto max-w-6xl">
            {loading && (
              <div className="rounded-lg border border-[#dbe5d2] bg-white p-10 text-center font-bold text-[#596959]">
                Loading certificate...
              </div>
            )}

            {!loading && error && (
              <div className="rounded-lg border border-red-200 bg-white p-10 text-center">
                <h1 className="text-3xl font-black text-red-700">Certificate not found</h1>
                <p className="mt-3 text-sm font-semibold text-[#596959]">{error}</p>
              </div>
            )}

            {!loading && certificate && (
              <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
                <article className="rounded-lg border border-[#dbe5d2] bg-white p-7 shadow-sm">
                  <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#987a1f]">Verified harvest lot</p>
                  <h1 className="mt-4 text-4xl font-black tracking-tight md:text-5xl">{certificate.lot_code}</h1>
                  <p className="mt-4 max-w-2xl text-base leading-7 text-[#596959]">
                    This certificate confirms a traceability record generated by eSanza for a registered crop lot.
                  </p>

                  <div className="mt-8 grid gap-3 sm:grid-cols-2">
                    {[
                      ['Crop', certificate.crop_type],
                      ['Variety', certificate.variety || 'Not specified'],
                      ['Harvest date', formatDate(certificate.harvest_date)],
                      ['Quality', certificate.quality_grade || 'Pending'],
                      ['Trees', String(certificate.tree_count || 0)],
                      ['Farmers', String(certificate.farmer_count || 0)],
                      ['Quantity', `${Number(certificate.total_quantity_kg || 0).toLocaleString()} kg`],
                      ['Status', String(certificate.traceability_status || 'draft').replace(/_/g, ' ')],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-lg bg-[#f7f9f4] p-4">
                        <p className="text-xs font-black uppercase tracking-[0.14em] text-[#7b8d7b]">{label}</p>
                        <p className="mt-2 text-lg font-black capitalize text-[#102016]">{value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 rounded-lg border border-emerald-200 bg-emerald-50 p-5">
                    <p className="text-sm font-black text-emerald-900">Verified by {certificate.verified_by || 'eSanza'}</p>
                    <p className="mt-1 text-sm leading-6 text-emerald-700">
                      Buyers and partners can use this page to confirm provenance, harvest details, and field evidence.
                    </p>
                  </div>
                </article>

                <aside className="rounded-lg border border-[#dbe5d2] bg-white p-7 shadow-sm">
                  <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#987a1f]">Trace timeline</p>
                  <div className="mt-6 space-y-4">
                    {timeline.length === 0 && (
                      <div className="rounded-lg bg-[#f7f9f4] p-5 text-sm font-semibold text-[#596959]">
                        Timeline events will appear as field teams verify this lot.
                      </div>
                    )}
                    {timeline.map((item, index) => (
                      <div key={`${item.event_type}-${index}`} className="grid grid-cols-[34px_1fr] gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#102016] text-xs font-black text-[#d4af37]">
                          {index + 1}
                        </span>
                        <div className="rounded-lg bg-[#f7f9f4] p-4">
                          <p className="font-black text-[#102016]">{item.event_title}</p>
                          <p className="mt-1 text-xs font-semibold text-[#7b8d7b]">{formatDate(item.event_date)}</p>
                          {item.event_description && <p className="mt-2 text-sm leading-6 text-[#596959]">{item.event_description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </aside>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
};

export default PublicTraceCertificatePage;
